import React, { useEffect, useState } from 'react';
import {
  emitDeliveryGuideDte,
  emitPurchaseDte,
  generateDteEmissionPdf,
  getDteEmissionArtifactUrl,
  getDeliveryGuideDteReadiness,
  getPurchaseDteReadiness,
  refreshDteEmissionStatus,
  retryDteEmission,
} from '../api';
import { getToken } from '../api/auth';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import InlineLoading from './InlineLoading';

const STATUS_LABELS = {
  reserved: 'Reservado',
  queued: 'En cola',
  processing: 'Procesando',
  generated: 'Generado',
  enveloped: 'Sobre generado',
  sent: 'Enviado',
  failed: 'Fallido',
  accepted: 'Aceptado',
  rejected: 'Rechazado',
};

const STATUS_CLASSES = {
  sent: 'bg-blue-50 text-blue-700 border-blue-100',
  accepted: 'bg-green-50 text-green-700 border-green-100',
  queued: 'bg-amber-50 text-amber-700 border-amber-100',
  processing: 'bg-amber-50 text-amber-700 border-amber-100',
  failed: 'bg-red-50 text-red-700 border-red-100',
  rejected: 'bg-red-50 text-red-700 border-red-100',
};

const DteEmissionPanel = ({ type, branchId, id, documentKind, className = 'bg-white p-4 rounded-lg shadow mb-6' }) => {
  const { user } = useCurrentUser();
  const canReadDte = hasPermission(user, 'dte.read');
  const canEmitDte = hasPermission(user, 'dte.emit');
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emitting, setEmitting] = useState(false);
  const [error, setError] = useState('');

  const loadReadiness = async () => {
    if (!canReadDte || !branchId || !id) return;
    setLoading(true);
    setError('');
    try {
      const data = type === 'deliveryGuide'
        ? await getDeliveryGuideDteReadiness(branchId, id)
        : await getPurchaseDteReadiness(branchId, id, documentKind ? { documentKind } : {});
      setReadiness(data);
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo revisar el estado DTE.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReadiness();
  }, [type, branchId, id, documentKind, canReadDte]);

  const handleEmit = async () => {
    setEmitting(true);
    setError('');
    try {
      await (
        type === 'deliveryGuide'
          ? emitDeliveryGuideDte(branchId, id)
          : emitPurchaseDte(branchId, id, documentKind ? { documentKind } : {})
      );
      await loadReadiness();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo emitir el DTE.');
      if (Array.isArray(err.response?.data?.details)) {
        setReadiness((prev) => ({ ...(prev || {}), issues: err.response.data.details, ready: false }));
      }
    } finally {
      setEmitting(false);
    }
  };

  const handleRefreshStatus = async () => {
    if (!emission?.id) return;
    setLoading(true);
    setError('');
    try {
      await refreshDteEmissionStatus(emission.id);
      await loadReadiness();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo actualizar el estado DTE.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    if (!emission?.id) return;
    setEmitting(true);
    setError('');
    try {
      await retryDteEmission(emission.id);
      await loadReadiness();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo reintentar el DTE.');
    } finally {
      setEmitting(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!emission?.id) return;
    setEmitting(true);
    setError('');
    try {
      await generateDteEmissionPdf(emission.id);
      await loadReadiness();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo generar el PDF DTE.');
    } finally {
      setEmitting(false);
    }
  };

  const handleDownloadArtifact = async (artifact) => {
    if (!emission?.id) return;
    setError('');
    try {
      const response = await fetch(getDteEmissionArtifactUrl(emission.id, artifact), {
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('No se pudo descargar el archivo DTE.');
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const fileName = disposition.match(/filename="([^"]+)"/)?.[1] || `dte-${artifact}`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'No se pudo descargar el archivo DTE.');
    }
  };

  if (!canReadDte) return null;

  const emission = readiness?.existingEmission;
  const statusClass = STATUS_CLASSES[emission?.status] || 'bg-gray-50 text-gray-700 border-gray-100';

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">DTE</h2>
          {readiness && (
            <p className="text-sm text-gray-600">
              {readiness.documentKind} tipo {readiness.dteType}
            </p>
          )}
        </div>
        {emission && (
          <span className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-semibold ${statusClass}`}>
            {STATUS_LABELS[emission.status] || emission.status}
            {emission.folio ? ` #${emission.folio}` : ''}
          </span>
        )}
      </div>

      {loading ? (
        <div className="mt-3">
          <InlineLoading label="Revisando DTE" />
        </div>
      ) : (
        <>
          {readiness?.ready ? (
            <p className="mt-3 text-sm text-green-700">Listo para emitir.</p>
          ) : (
            <div className="mt-3">
              <p className="text-sm font-semibold text-red-700">Pendiente para emitir</p>
              <ul className="mt-1 list-disc pl-5 text-sm text-red-700">
                {(readiness?.issues || []).map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </div>
          )}
          {emission?.trackId && (
            <p className="mt-2 text-sm text-gray-600">TrackID: {emission.trackId}</p>
          )}
          {emission?.nextAttemptAt && ['queued', 'processing'].includes(emission.status) && (
            <p className="mt-2 text-sm text-gray-600">
              Proximo intento: {new Date(emission.nextAttemptAt).toLocaleString()}
            </p>
          )}
          {emission?.retryCount > 0 && (
            <p className="mt-2 text-sm text-gray-600">
              Reintentos: {emission.retryCount} / {emission.maxRetries}
            </p>
          )}
          {emission?.errorMessage && (
            <p className="mt-2 text-sm text-red-700">{emission.errorMessage}</p>
          )}
          {emission?.artifacts && (
            <div className="mt-3 flex flex-wrap gap-2">
              {emission.artifacts.dteXmlConfigured && (
                <button type="button" onClick={() => handleDownloadArtifact('dte')} className="px-3 py-1 rounded border border-gray-300 text-sm hover:bg-gray-50">
                  XML DTE
                </button>
              )}
              {emission.artifacts.envelopeXmlConfigured && (
                <button type="button" onClick={() => handleDownloadArtifact('envelope')} className="px-3 py-1 rounded border border-gray-300 text-sm hover:bg-gray-50">
                  XML Envio
                </button>
              )}
              {emission.artifacts.pdfConfigured && (
                <button type="button" onClick={() => handleDownloadArtifact('pdf')} className="px-3 py-1 rounded border border-gray-300 text-sm hover:bg-gray-50">
                  PDF
                </button>
              )}
            </div>
          )}
          {Array.isArray(emission?.statusHistory) && emission.statusHistory.length > 0 && (
            <details className="mt-3 text-sm text-gray-600">
              <summary className="cursor-pointer font-medium">Historial DTE</summary>
              <ul className="mt-2 space-y-1">
                {emission.statusHistory.slice(-6).reverse().map((event, index) => (
                  <li key={`${event.at}-${event.status}-${index}`}>
                    {new Date(event.at).toLocaleString()} - {STATUS_LABELS[event.status] || event.status}
                    {event.message ? `: ${event.message}` : ''}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
          <div className="mt-4 flex flex-wrap gap-2">
          {canEmitDte && !emission && (
            <button
              type="button"
              onClick={handleEmit}
              disabled={!readiness?.ready || emitting}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {emitting ? 'Encolando...' : 'Emitir DTE'}
            </button>
          )}
          {emission?.status === 'sent' && (
            <button
              type="button"
              onClick={handleRefreshStatus}
              disabled={loading}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
            >
              Actualizar estado SII
            </button>
          )}
          {canEmitDte && emission?.artifacts?.dteXmlConfigured && !emission?.artifacts?.pdfConfigured && (
            <button
              type="button"
              onClick={handleGeneratePdf}
              disabled={emitting}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
            >
              {emitting ? 'Generando...' : 'Generar PDF'}
            </button>
          )}
          {canEmitDte && emission?.status === 'failed' && (
            <button
              type="button"
              onClick={handleRetry}
              disabled={emitting}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {emitting ? 'Reintentando...' : 'Reintentar'}
            </button>
          )}
          </div>
        </>
      )}
    </div>
  );
};

export default DteEmissionPanel;
